import { NextRequest } from 'next/server';

import { LOCAL_STORAGE_LAST_ACTIVE } from '@/constants/constants';
import { BASE_URL, LOGIN_URL, MY_ACRES_URL } from '@/constants/urls';
import { FeatureHubSessionConfig, Session } from '@/ory/models/session';

const ONE_HOUR = 3600 * 1000;

// is within <numHoursTilExpiration> hours of sessions expiring
const isCloseToSessionExpire = (
  now: number,
  session: Session,
  orySessionConfig: FeatureHubSessionConfig
): boolean => {
  if (session && orySessionConfig?.numHoursTilExpiration) {
    const hoursAllowedTilExpire =
      orySessionConfig.numHoursTilExpiration * ONE_HOUR;
    const expiresAt: number = new Date(session.expires_at).getTime();
    const timeTilExpiration = expiresAt - now;
    return timeTilExpiration < hoursAllowedTilExpire;
  }
  return false;
};

// has been inactive for <numHoursAllowedInactive> hours or more
const isInactiveRequiredTime = (
  now: number,
  orySessionConfig: FeatureHubSessionConfig
): boolean => {
  if (orySessionConfig?.numHoursAllowedInactive) {
    const hoursAllowedInactive =
      orySessionConfig.numHoursAllowedInactive * ONE_HOUR;
    const lastActive: number = parseInt(
      localStorage.getItem(LOCAL_STORAGE_LAST_ACTIVE) || '',
      10
    );
    const timeInactive = now - lastActive;
    return timeInactive > hoursAllowedInactive;
  }
  return false;
};

/**
 * Logic to improve session expiration experience
 * @param orySessionConfig Session config from FeatureHub
 * @param session The user's session
 * @param now UTC time in milliseconds
 * @returns true when...
 *  - They are within configured hours of sessions expiring
 *  - And they have been inactive for configured hours or more
 */
export const shouldLogoutEarly = (
  orySessionConfig: FeatureHubSessionConfig | undefined,
  session: Session | undefined,
  now: number
): boolean => {
  if (
    session &&
    orySessionConfig?.numHoursAllowedInactive &&
    orySessionConfig?.numHoursTilExpiration
  ) {
    return (
      isCloseToSessionExpire(now, session, orySessionConfig) &&
      isInactiveRequiredTime(now, orySessionConfig)
    );
  }
  return false;
};

export const isUserAnonymous = (req: NextRequest): boolean => {
  const token = req.cookies.get('ory_kratos_session');
  return !token || !token.value;
};

export const shouldMakeUserLogIn = (req: NextRequest): boolean => {
  if (!isUserAnonymous(req)) return false;

  const loggedInUserOnlyUrls = [MY_ACRES_URL];

  return loggedInUserOnlyUrls.some((url) =>
    req.nextUrl.pathname.startsWith(url)
  );
};

export const getLogInRedirectUrl = (req: NextRequest): string => {
  return `${BASE_URL}${LOGIN_URL}?from=${req.nextUrl.pathname}`;
};
